/* eslint-disable indent */
import React, { useEffect } from 'react';
import { FormData, FormErrors, IUpsertContactDialog } from './interfaces';
import {
  AppBar,
  Button,
  CustomTheme,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  MenuItem,
  SelectChangeEvent,
  Stack,
  TextField,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';

import { useNavigate } from 'react-router';
import {
  isEmpty,
  isValidEmailAddress,
  isValidName,
  isValidPhone,
} from '../../../../helpers/formHelpers';
import useTrackFormChanges from '../../../../hooks/useTrackFormChanges';
import useFormState from '../../../../hooks/useFormState';
import { MutateOptions } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { enqueueSnackbar } from 'notistack';
import {
  useCreateContactMutation,
  useGetJobFunctionsList,
  useUpdateContactMutation,
} from '../../../../reactQuery/contacts';
import { ICreateContactParams } from '../../../../api/contactsApi/interfaces';
import { TagsField } from '../../../../components/forms/TagsField';
import { useGetClientsList } from '../../../../reactQuery/clients';
import { ValidationErrorResponse } from '../../../../api/interfaces';
import { SelectField } from '../../../../components/forms';

export const UpsertContactDialog: React.FC<IUpsertContactDialog> = ({
  show,
  onClose,
  contact,
}): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const theme = useTheme<CustomTheme>();
  const fullScreen = useMediaQuery(theme.breakpoints.down('tablet'));
  const isEditMode = !!contact;

  const { formData, setFormFieldData, setFormData } = useFormState<FormData>(
    {}
  );
  const { formErrors, setFormErrors } = useTrackFormChanges(formData);

  const { mutate: createContact, isLoading: isCreatingContact } =
    useCreateContactMutation();
  const { mutate: updateContact, isLoading: isUpdatingContact } =
    useUpdateContactMutation();

  const {
    data: clientsList,
    isLoading: loadingClientsList,
    error: clientsListError,
  } = useGetClientsList();

  const {
    data: jobFunctionsList,
    isLoading: loadingJobFunctionsList,
    error: jobFunctionsListError,
  } = useGetJobFunctionsList();

  const validateForm = (): void => {
    const errors: FormErrors = {};

    if (!formData.firstName || isEmpty(formData.firstName)) {
      errors.firstName = t('errors.required');
    } else if (!isValidName(formData.firstName)) {
      errors.firstName = t('errors.invalidName');
    }

    if (formData.lastName && !isValidName(formData.lastName)) {
      errors.lastName = t('errors.invalidName');
    }

    if (formData.email && !isValidEmailAddress(formData.email)) {
      errors.email = t('errors.invalidEmail');
    }

    if (formData.phonenumber && !isValidPhone(formData.phonenumber)) {
      errors.phonenumber = t('errors.invalidPhonenumber');
    }

    if (!formData.clients || !formData.clients.length) {
      errors.clients = t('errors.required');
    }

    setFormErrors(errors);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!Object.keys(formErrors).length) {
      const contactData: ICreateContactParams = {
        firstName: formData.firstName as string,
        lastName: formData.lastName ? (formData.lastName as string) : null,
        email: formData.email ? (formData.email as string) : null,
        phonenumber: formData.phonenumber
          ? (formData.phonenumber as string)
          : null,
        clients: formData.clients as Array<number>,
        jobFunctions: formData.jobFunctions,
        contactInterval:
          formData.contactInterval === 0 ? undefined : formData.contactInterval,
      };

      const mutateOptions:
        | MutateOptions<number, AxiosError, unknown>
        | undefined = {
        onSuccess: (contactId) => {
          enqueueSnackbar(
            isEditMode
              ? t('forms:updateContact.success')
              : t('forms:createContact.success'),
            {
              variant: 'success',
            }
          );

          navigate(`/contacts/${contactId}`);
          onClose();
        },
        onError: (response) => {
          // TODO: refactor this into helper function.
          const axiosErrorResponse =
            response as AxiosError<ValidationErrorResponse>;

          if (axiosErrorResponse.status === 422) {
            const errors = axiosErrorResponse.response?.data.errors;

            if (errors) {
              Object.entries(errors).forEach(([field, messages]) => {
                if (messages.length > 0) {
                  formErrors[field] = messages[0];
                }
              });
            }
          }
          // ---

          enqueueSnackbar(
            isEditMode
              ? t('forms:updateContact.failed')
              : t('forms:createContact.failed'),
            {
              variant: 'error',
            }
          );
        },
      };

      if (isEditMode) {
        updateContact(
          {
            contactId: contact.id,
            contactData,
          },
          mutateOptions
        );
      } else {
        createContact(contactData, mutateOptions);
      }
    }
  };

  useEffect(() => {
    setFormErrors({});

    if (contact) {
      setFormData({
        firstName: contact.firstName,
        lastName: contact.lastName || undefined,
        email: contact.email || undefined,
        phonenumber: contact.phonenumber || undefined,
        jobFunctions: contact.jobFunctions?.map(
          (jobFunction) => jobFunction.id
        ),
        clients: contact.clients?.map((client) => client.id),
        contactInterval: contact.contactInterval,
      });
    } else {
      setFormData({});
    }
  }, [show]);

  return (
    <Dialog
      fullScreen={fullScreen}
      open={show}
      keepMounted={false}
      //   TransitionComponent={Transition}
    >
      <AppBar sx={{ position: 'relative' }}>
        <Toolbar>
          <Typography sx={{ flex: 1 }} variant="h6" component="div">
            {isEditMode
              ? t('forms:updateContact.title')
              : t('forms:createContact.title')}
          </Typography>
          <IconButton
            edge="end"
            color="inherit"
            disabled={isCreatingContact || isUpdatingContact}
            onClick={onClose}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <DialogContent
        dividers={true}
        sx={{
          minWidth: fullScreen ? undefined : '400px',
        }}
      >
        <Stack
          gap="15px"
          component="form"
          id="createAgentForm"
          onSubmit={handleSubmit}
        >
          <TextField
            label={t('entities:common.firstName')}
            variant="outlined"
            size="small"
            value={formData.firstName}
            placeholder={contact?.firstName}
            onChange={(e) => setFormFieldData('firstName', e.target.value)}
            error={!!formErrors.firstName}
            helperText={formErrors.firstName}
            required
          />

          <TextField
            label={t('entities:common.lastName')}
            variant="outlined"
            size="small"
            value={formData.lastName}
            placeholder={contact?.lastName || undefined}
            onChange={(e) => setFormFieldData('lastName', e.target.value)}
            error={!!formErrors.lastName}
            helperText={formErrors.lastName}
          />

          <TextField
            label={t('entities:common.email')}
            variant="outlined"
            size="small"
            value={formData.email}
            placeholder={contact?.email || undefined}
            onChange={(e) => setFormFieldData('email', e.target.value)}
            error={!!formErrors.email}
            helperText={formErrors.email}
            type="email"
          />

          <TextField
            label={t('entities:contact.phonenumber')}
            variant="outlined"
            size="small"
            value={formData.phonenumber}
            placeholder={contact?.phonenumber || undefined}
            onChange={(e) => setFormFieldData('phonenumber', e.target.value)}
            error={!!formErrors.phonenumber}
            helperText={formErrors.phonenumber}
          />

          <TagsField
            existingTags={clientsList}
            label={t('entities:contact.clients')}
            value={formData.clients || []}
            error={formErrors.clients}
            onChange={(value) => {
              setFormFieldData('clients', value);
            }}
            sx={{
              maxWidth: fullScreen ? undefined : '400px',
            }}
          />

          <TagsField
            existingTags={jobFunctionsList}
            label={t('entities:contact.jobFunction')}
            value={formData.jobFunctions || []}
            onChange={(value) => {
              setFormFieldData('jobFunctions', value);
            }}
            sx={{
              maxWidth: fullScreen ? undefined : '400px',
            }}
            createTags
          />

          <SelectField
            name="contactInterval"
            label={t('entities:contact.contactInterval')}
            value={
              formData.contactInterval ? String(formData.contactInterval) : '0'
            }
            onChange={(event: SelectChangeEvent) =>
              setFormFieldData('contactInterval', Number(event.target.value))
            }
            required
          >
            <MenuItem key="none" value="0">
              {t('forms:createContact.fields.contactInterval.noInterval')}
            </MenuItem>
            {Array.from({ length: 12 }, (_, index) => (
              <MenuItem key={index + 1} value={(index + 1) * 30}>
                {t('words.month', { count: index + 1 })}
              </MenuItem>
            ))}
          </SelectField>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          disabled={isCreatingContact || isUpdatingContact}
        >
          {t('buttons.cancel')}
        </Button>
        <Button
          form="createAgentForm"
          type="submit"
          variant="contained"
          onClick={validateForm}
          disabled={isCreatingContact || isUpdatingContact}
        >
          {isEditMode ? t('buttons.update') : t('buttons.create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
